// Where a browser export is written (#367, spec E2 decision 28): a file of the browser's own,
// origin-private file system (OPFS), so a long 4K export streams to disk instead of filling the
// tab's memory. The bell saves it again from there; Clear deletes it, and an editor that opens
// deletes the ones a day old (another tab's may still be in its bell).

/** The folder the editor's exports go in, inside the origin's private file system. */
const FOLDER = "editor-exports";

/** Whether this browser can stream an export to its own file system. */
export function opfsAvailable(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.storage?.getDirectory === "function" &&
    typeof FileSystemFileHandle !== "undefined" &&
    "createWritable" in FileSystemFileHandle.prototype
  );
}

/**
 * Whether this browser's file system takes an export now: a browser that blocks site data refuses
 * its storage (`getDirectory` rejects), and its export runs in memory as before.
 */
export async function opfsUsable(): Promise<boolean> {
  if (!opfsAvailable()) return false;
  try {
    await folder();
    return true;
  } catch {
    return false;
  }
}

/** How many bytes the browser's storage still has room for, or null when it does not say. */
export async function roomLeft(): Promise<number | null> {
  try {
    const { quota, usage } = await navigator.storage.estimate();
    return quota === undefined ? null : Math.max(0, quota - (usage ?? 0));
  } catch {
    return null;
  }
}

async function folder(): Promise<FileSystemDirectoryHandle> {
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(FOLDER, { create: true });
}

/** A new export file: its stream to write, and the file once written. */
export interface ExportFile {
  writable: FileSystemWritableFileStream;
  /** The written file, read from disk (not held in memory). */
  read: () => Promise<File>;
  /** Deletes it (a failed or canceled export). */
  remove: () => Promise<void>;
}

/** Opens `name` for writing, empty, in the exports folder. */
export async function createExportFile(name: string): Promise<ExportFile> {
  const dir = await folder();
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable({ keepExistingData: false });
  return {
    writable,
    read: () => handle.getFile(),
    remove: () => dir.removeEntry(name).catch(() => undefined),
  };
}

/** Deletes these export files (the bell's Clear; another tab's are left alone). */
export async function removeExportFiles(names: readonly string[]): Promise<void> {
  if (!opfsAvailable() || names.length === 0) return;
  try {
    const dir = await folder();
    await Promise.all(names.map((name) => dir.removeEntry(name).catch(() => undefined)));
  } catch {
    // A browser that refuses its storage keeps nothing there anyway.
  }
}

/** How old a left-over export file is before an editor that opens deletes it. */
const STALE_MS = 24 * 60 * 60 * 1000;

/** Deletes the export files written more than a day ago (left by an editor closed since). */
export async function removeStaleExportFiles(now = Date.now()): Promise<void> {
  if (!opfsAvailable()) return;
  try {
    const dir = await folder();
    for await (const [name, handle] of dir.entries()) {
      if (handle.kind !== "file") continue;
      const file = await (handle as FileSystemFileHandle).getFile();
      if (now - file.lastModified > STALE_MS) await dir.removeEntry(name).catch(() => undefined);
    }
  } catch {
    // Nothing to tidy where the storage is refused.
  }
}
